import React, { useEffect, useMemo } from "react";
import { Link, Outlet, useLocation } from "react-router-dom";
import DocsHeader from "./DocsHeader";
import DocsSidebar, { SidebarItem } from "./DocsSidebar";
import DocsTOC, { TOCItem } from "./DocsTOC";

interface DocumentationLayoutProps {
  sidebarItems: SidebarItem[];
  tocItems: TOCItem[];
  breadcrumbs?: { label: string; href?: string }[];
}

const DocumentationLayout = ({
  sidebarItems,
  tocItems,
  breadcrumbs = []
}: DocumentationLayoutProps) => {
  const location = useLocation();

  // Scroll to top or hash on route change
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      window.scrollTo(0, 0);
    }
  }, [location.pathname, location.hash]);

  // Flatten sidebar items to find next/prev pages
  const flatItems = useMemo(() => {
    return sidebarItems.filter(item => !item.isHeader && item.href.startsWith('/'));
  }, [sidebarItems]);

  const currentIndex = flatItems.findIndex(item => item.href === location.pathname);
  const prevPage = currentIndex > 0 ? flatItems[currentIndex - 1] : null;
  const nextPage = currentIndex < flatItems.length - 1 ? flatItems[currentIndex + 1] : null;

  return (
    <div className="min-h-screen bg-white text-[#111] font-sans selection:bg-[#0070f3]/10 selection:text-[#0070f3]">
      <DocsHeader />
      
      <div className="mx-auto flex max-w-[1400px] px-6">
        {/* Left Sidebar */}
        <aside className="sticky top-16 hidden h-[calc(100vh-64px)] w-64 flex-shrink-0 overflow-y-auto border-r border-[#eaeaea] py-8 pr-6 lg:block custom-scrollbar">
          <DocsSidebar items={sidebarItems} />
        </aside>

        {/* Main Content Area */}
        <main className="flex-1 min-w-0 py-12 lg:px-12 xl:px-16">
          <div className="mx-auto max-w-[900px]">
            {/* Breadcrumbs */}
            {breadcrumbs.length > 0 && (
              <nav className="mb-6 flex items-center gap-2 text-sm text-slate-500 overflow-x-auto whitespace-nowrap">
                {breadcrumbs.map((crumb, idx) => (
                  <React.Fragment key={idx}>
                    {idx > 0 && <span className="text-slate-300">/</span>}
                    {crumb.href ? (
                      <Link to={crumb.href} className="hover:text-black transition-colors">
                        {crumb.label}
                      </Link>
                    ) : (
                      <span className="font-medium text-black">{crumb.label}</span>
                    )}
                  </React.Fragment>
                ))}
              </nav>
            )}

            {/* Content Body */}
            <article className="prose prose-slate max-w-none 
              prose-headings:text-black prose-headings:font-bold
              prose-h1:text-[40px] prose-h1:leading-tight prose-h1:mb-8 prose-h1:tracking-tighter
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-4 prose-h2:border-b prose-h2:border-[#eaeaea] prose-h2:pb-2
              prose-p:text-slate-600 prose-p:leading-relaxed prose-p:mb-6
              prose-a:text-[#0070f3] prose-a:no-underline hover:prose-a:underline
              prose-ul:text-slate-600 prose-li:mb-2
              prose-code:text-black prose-code:bg-[#fafafa] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:border prose-code:border-[#eaeaea] prose-code:before:content-none prose-code:after:content-none
              prose-pre:p-0 prose-pre:bg-transparent prose-pre:static">
              <Outlet />
            </article>

            {/* Pagination Cards */}
            {(prevPage || nextPage) && (
              <div className="mt-16 flex items-center justify-between border-t border-[#eaeaea] pt-10">
                {prevPage ? (
                  <Link to={prevPage.href} className="flex flex-col gap-1 group no-underline">
                    <span className="text-xs font-medium text-slate-400">Previous</span>
                    <span className="text-sm font-bold text-[#0070f3] group-hover:underline">← {prevPage.title}</span>
                  </Link>
                ) : <div />}
                
                {nextPage ? (
                  <Link to={nextPage.href} className="flex flex-col items-end gap-1 group text-right no-underline">
                    <span className="text-xs font-medium text-slate-400">Next</span>
                    <span className="text-sm font-bold text-[#0070f3] group-hover:underline">{nextPage.title} →</span>
                  </Link>
                ) : <div />}
              </div>
            )}
          </div>
        </main>

        {/* Right TOC */}
        <aside className="sticky top-16 hidden h-[calc(100vh-64px)] w-60 flex-shrink-0 overflow-y-auto py-12 pl-6 xl:block custom-scrollbar">
          <DocsTOC items={tocItems} />
        </aside>
      </div>

      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #eaeaea;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #d1d1d1;
        }
      `}</style>
    </div>
  );
};

export default DocumentationLayout;
